import { Fragment, Suspense, lazy } from 'react';
import { useSelector } from 'react-redux';
import { Redirect, Route, Switch, useLocation } from 'react-router';
import { CSSTransition, TransitionGroup } from 'react-transition-group';

import { Spinner } from '../components/data/Spinner';
import AdminGuard from '../components/guards/AdminGuard';
import LoggedInGuard from '../components/guards/LoggedInGuard';
import LoginPage from '../containers/LoginPage';
import MonitorPage from '../containers/MonitorPage';
import NewShipmentPage, {
  NewSingleShipmentPage,
} from '../containers/NewShipmentPage';
import { EditQuotePage, NewQuotePage } from '../containers/NewQuotePage';
import PasswordResetPage from '../containers/PasswordResetPage';
import {
  RepeatShipmentPage,
  ReverseShipmentPage,
} from '../containers/CopyShipmentPage';
import { SharedShipmentDetailPage } from '../containers/ShipmentDetailPage';
import SignupPage from '../containers/SignupPage';
import AdminAccountManagementPage from '../containers/admin/AdminAccountManagementPage';
import AdminBusinessTypePage from '../containers/admin/AdminBusinessTypePage';
import AdminConsolePage from '../containers/admin/AdminConsolePage';
import AdminSystemSettingsPage from '../containers/admin/AdminSystemSettingsPage';
import AdminUserManagementPage from '../containers/admin/AdminUserManagementPage';
import LinkDeviceToJobPage from '../containers/LinkDeviceToJobPage';
import DownloadReport from '../containers/reports/Download';
import { cssVariables } from '../styles/cssVariables';
import { msToNumber } from '../utils/cssUtils';
import { cssTransitionClassNames } from '../utils/reactTransitionUtils';
import { useLoginPageRedirectRoute } from '../utils/redirectUtils';
import { sortRoutes } from '../utils/routeUtils';
import { getLoggedIn } from './redux/auth';
import routes from './routes';

function MonitorRedirect() {
  return <Redirect to={routes.monitor} />;
}

function AdminLegacyRedirect() {
  return <Redirect to={routes.admin.legacy.userManagement} />;
}

const mappings = sortRoutes([
  {
    path: routes.root,
    component: MonitorRedirect,
    exact: true,
    loginGuard: true,
  },
  { path: routes.monitor, component: MonitorPage, loginGuard: true },
  {
    path: routes.sharingShipmentDetail,
    component: SharedShipmentDetailPage,
  },
  {
    path: routes.book.newShipment,
    component: NewShipmentPage,
    loginGuard: true,
  },
  {
    path: routes.book.newShipmentSingle,
    component: NewSingleShipmentPage,
    loginGuard: true,
  },
  {
    path: routes.book.newAirRetrieval,
    component: NewShipmentPage,
    loginGuard: true,
  },
  {
    path: routes.reverseShipment,
    component: ReverseShipmentPage,
    loginGuard: true,
  },
  {
    path: routes.repeatShipment,
    component: RepeatShipmentPage,
    loginGuard: true,
  },
  {
    path: routes.quoting.editQuote,
    component: EditQuotePage,
    loginGuard: true,
  },
  {
    path: routes.quoting.newQuote,
    component: NewQuotePage,
    loginGuard: true,
  },
  {
    path: routes.reports.reportDetail,
    component: DownloadReport,
    withRedirect: true,
  },
  {
    path: routes.tools.addressBookImport,
    lazyComponent: lazy(() => import('../containers/AddressBookImportPage')),
    loginGuard: true,
  },
  {
    path: routes.tools.timeoutTest,
    lazyComponent: lazy(() => import('../containers/TimeoutTestPage')),
    loginGuard: true,
  },
  { path: routes.login, component: LoginPage },
  { path: routes.signup, component: SignupPage },
  { path: routes.passwordReset, component: PasswordResetPage },
  {
    path: routes.admin.legacy.prefix,
    component: AdminLegacyRedirect,
    exact: true,
    adminGuard: true,
  },
  {
    path: routes.admin.legacy.userManagement,
    component: AdminUserManagementPage,
    adminGuard: true,
  },
  {
    path: routes.admin.legacy.accountManagement,
    component: AdminAccountManagementPage,
    adminGuard: true,
  },
  {
    path: routes.admin.legacy.businessType,
    component: AdminBusinessTypePage,
    adminGuard: true,
  },
  {
    path: routes.admin.legacy.systemSettings,
    component: AdminSystemSettingsPage,
    adminGuard: true,
  },
  {
    path: routes.admin.prefix,
    component: AdminConsolePage,
    adminGuard: true,
  },
  {
    path: routes.linkDeviceToJob,
    component: LinkDeviceToJobPage,
  },
]);

const animations = [
  { path: routes.admin.legacy, exact: false, animationName: 'from-left' },
];

const DefaultRoute = () => {
  const loggedIn = useSelector(getLoggedIn);
  const loginRedirectUrl = useLoginPageRedirectRoute();
  return <Redirect to={loggedIn ? routes.root : loginRedirectUrl} />;
};

const AdminAndLoggedInGuard = ({ children }) => (
  <LoggedInGuard>
    <AdminGuard>{children}</AdminGuard>
  </LoggedInGuard>
);

const RedirectAfterLogin = ({ component: Component }) => {
  const location = useLocation();
  const loggedIn = useSelector(getLoggedIn);
  const loginRedirectUrl = useLoginPageRedirectRoute();

  return loggedIn ? (
    <Component />
  ) : (
    <Redirect
      to={{
        pathname: loginRedirectUrl,
        state: { from: location },
      }}
    />
  );
};

function renderRouteChildren({
  loginGuard,
  adminGuard,
  component,
  lazyComponent,
}) {
  let OuterGuard = Fragment;
  if (loginGuard) {
    OuterGuard = LoggedInGuard;
  }
  if (adminGuard) {
    OuterGuard = AdminAndLoggedInGuard;
  }

  const InnerGuard = lazyComponent ? Suspense : Fragment;
  const innerGuardProps = lazyComponent ? { fallback: <Spinner /> } : {};
  const Page = lazyComponent || component;

  return routeProps => (
    <OuterGuard>
      <InnerGuard {...innerGuardProps}>
        <Page {...routeProps} />
      </InnerGuard>
    </OuterGuard>
  );
}

function getAnimationDefinition(pathname) {
  return animations.find(({ path, exact }) =>
    exact ? pathname === path : pathname.startsWith(path)
  );
}

function useAnimationProps(anim) {
  return {
    timeout: msToNumber(cssVariables.animationDurationMd),
    classNames: {
      ...(anim
        ? cssTransitionClassNames(anim.animationName, {
            appear: false,
            enter: true,
            exit: true,
          })
        : {}),
    },
  };
}

export default function AppRoutes() {
  const location = useLocation();
  const anim = getAnimationDefinition(location.pathname);
  const animationProps = useAnimationProps(anim);

  return (
    <TransitionGroup component={null}>
      <CSSTransition key={anim?.path || 'no-anim'} {...animationProps}>
        <Switch location={location}>
          {mappings.map(({ path, exact, withRedirect, ...info }) => (
            <Route key={path} path={path} exact={exact}>
              {withRedirect ? (
                <RedirectAfterLogin {...info} />
              ) : (
                renderRouteChildren(info)
              )}
            </Route>
          ))}
          <Route path="*" component={DefaultRoute} />
        </Switch>
      </CSSTransition>
    </TransitionGroup>
  );
}
